import {
	InspectionTemplate,
	InspectionTemplateDraft,
} from "@/shared/models/Inspection";
import { SectionRevisions } from "./templateDrafts";

export type TemplateIssue =
	| { kind: "rev-taken"; rev: number }
	| { kind: "rev-behind"; rev: number; highest: number }
	| {
			kind: "rev-out-of-order";
			rev: number;
			before: InspectionTemplate | null;
			after: InspectionTemplate | null;
	  }
	| { kind: "effective-before-latest"; rev: number; takesOverOn: Date }
	| {
			kind: "effective-out-of-order";
			before: InspectionTemplate | null;
			after: InspectionTemplate | null;
	  };

type Neighbours = {
	previous: InspectionTemplate | null;
	next: InspectionTemplate | null;
};

const revIssue = (
	revisions: SectionRevisions,
	draft: InspectionTemplateDraft,
	{ previous, next }: Neighbours,
	isNewRevision: boolean,
): TemplateIssue | null => {
	const rev = draft.Rev;

	if (rev === null) return null;

	if (revisions.isTaken(rev, draft.TemplateId))
		return { kind: "rev-taken", rev };

	if (isNewRevision && revisions.isBehind(rev))
		return { kind: "rev-behind", rev, highest: revisions.highest };

	if (
		(previous !== null && rev <= previous.Rev) ||
		(next !== null && rev >= next.Rev)
	)
		return { kind: "rev-out-of-order", rev, before: previous, after: next };

	return null;
};

const effectiveFromIssue = (
	revisions: SectionRevisions,
	draft: InspectionTemplateDraft,
	{ previous, next }: Neighbours,
	isNewRevision: boolean,
): TemplateIssue | null => {
	const effectiveFrom = draft.EffectiveFrom;

	if (effectiveFrom === null) return null;

	const latest = revisions.latest;

	if (
		isNewRevision &&
		latest?.EffectiveFrom != null &&
		revisions.startsBeforeLatest(effectiveFrom)
	)
		return {
			kind: "effective-before-latest",
			rev: latest.Rev,
			takesOverOn: latest.EffectiveFrom,
		};

	if (
		(previous?.EffectiveFrom != null &&
			effectiveFrom < previous.EffectiveFrom) ||
		(next?.EffectiveFrom != null && effectiveFrom > next.EffectiveFrom)
	)
		return { kind: "effective-out-of-order", before: previous, after: next };

	return null;
};

export const validateDraft = (
	revisions: SectionRevisions,
	draft: InspectionTemplateDraft,
	original: InspectionTemplateDraft,
) => {
	const isNewRevision = draft.TemplateId === null;

	const neighbours = isNewRevision
		? { previous: null, next: null }
		: revisions.neighbours(original.Rev, draft.TemplateId);

	return {
		rev: revIssue(revisions, draft, neighbours, isNewRevision),
		effectiveFrom: effectiveFromIssue(
			revisions,
			draft,
			neighbours,
			isNewRevision,
		),
	};
};
